import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ProtectedRoute } from "@/components/layout/ProtectedRoute";

// Marketing
import Home from "@/pages/marketing/Home";
import About from "@/pages/marketing/About";
import SalaryAdvance from "@/pages/marketing/SalaryAdvance";
import PersonalLoan from "@/pages/marketing/PersonalLoan";
import Eligibility from "@/pages/marketing/Eligibility";
import LoanCalculator from "@/pages/marketing/LoanCalculator";
import EMICalculator from "@/pages/marketing/EMICalculator";
import FAQs from "@/pages/marketing/FAQs";
import Blog from "@/pages/marketing/Blog";
import Careers from "@/pages/marketing/Careers";
import Contact from "@/pages/marketing/Contact";
import { Privacy, Terms, Disclaimer } from "@/pages/marketing/Legal";
import Grievance from "@/pages/marketing/Grievance";

// Auth
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";

// Customer
import CustomerDashboard from "@/pages/customer/Dashboard";
import ApplyLoan from "@/pages/customer/ApplyLoan";
import LoanStatus from "@/pages/customer/LoanStatus";
import ActiveLoans from "@/pages/customer/ActiveLoans";
import PreviousLoans from "@/pages/customer/PreviousLoans";
import Repayment from "@/pages/customer/Repayment";
import Statements from "@/pages/customer/Statements";
import Notifications from "@/pages/customer/Notifications";
import Profile from "@/pages/customer/Profile";
import Settings from "@/pages/customer/Settings";
import HelpCentre from "@/pages/customer/HelpCentre";

// Admin
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminCustomers from "@/pages/admin/Customers";
import AdminLoans from "@/pages/admin/LoanApplications";
import AdminKYC from "@/pages/admin/KYCReview";
import AdminReports from "@/pages/admin/Reports";
import AdminCMS from "@/pages/admin/CMS";
import AdminUsers from "@/pages/admin/UserManagement";
import AdminRoles from "@/pages/admin/Roles";
import AdminAudit from "@/pages/admin/AuditLogs";
import AdminSettings from "@/pages/admin/Settings";

function App() {
  useEffect(() => { document.title = "Finzpe · Premium digital lending"; }, []);
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <div className="App">
            <Routes>
              {/* Marketing */}
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/salary-advance" element={<SalaryAdvance />} />
              <Route path="/personal-loan" element={<PersonalLoan />} />
              <Route path="/eligibility" element={<Eligibility />} />
              <Route path="/loan-calculator" element={<LoanCalculator />} />
              <Route path="/emi-calculator" element={<EMICalculator />} />
              <Route path="/faqs" element={<FAQs />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/disclaimer" element={<Disclaimer />} />
              <Route path="/grievance" element={<Grievance />} />

              {/* Auth */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />

              {/* Customer */}
              <Route path="/app" element={<ProtectedRoute><CustomerDashboard /></ProtectedRoute>} />
              <Route path="/app/apply" element={<ProtectedRoute><ApplyLoan /></ProtectedRoute>} />
              <Route path="/app/loans" element={<ProtectedRoute><LoanStatus /></ProtectedRoute>} />
              <Route path="/app/active" element={<ProtectedRoute><ActiveLoans /></ProtectedRoute>} />
              <Route path="/app/history" element={<ProtectedRoute><PreviousLoans /></ProtectedRoute>} />
              <Route path="/app/repayment" element={<ProtectedRoute><Repayment /></ProtectedRoute>} />
              <Route path="/app/statements" element={<ProtectedRoute><Statements /></ProtectedRoute>} />
              <Route path="/app/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
              <Route path="/app/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/app/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path="/app/help" element={<ProtectedRoute><HelpCentre /></ProtectedRoute>} />

              {/* Admin */}
              <Route path="/admin" element={<ProtectedRoute admin><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/customers" element={<ProtectedRoute admin><AdminCustomers /></ProtectedRoute>} />
              <Route path="/admin/loans" element={<ProtectedRoute admin><AdminLoans /></ProtectedRoute>} />
              <Route path="/admin/kyc" element={<ProtectedRoute admin><AdminKYC /></ProtectedRoute>} />
              <Route path="/admin/underwriting" element={<ProtectedRoute admin><AdminLoans filterStatus="submitted" /></ProtectedRoute>} />
              <Route path="/admin/disbursement" element={<ProtectedRoute admin><AdminLoans filterStatus="approved" /></ProtectedRoute>} />
              <Route path="/admin/repayment" element={<ProtectedRoute admin><AdminReports /></ProtectedRoute>} />
              <Route path="/admin/reports" element={<ProtectedRoute admin><AdminReports /></ProtectedRoute>} />
              <Route path="/admin/cms" element={<ProtectedRoute admin><AdminCMS /></ProtectedRoute>} />
              <Route path="/admin/users" element={<ProtectedRoute admin><AdminUsers /></ProtectedRoute>} />
              <Route path="/admin/roles" element={<ProtectedRoute admin><AdminRoles /></ProtectedRoute>} />
              <Route path="/admin/audit" element={<ProtectedRoute admin><AdminAudit /></ProtectedRoute>} />
              <Route path="/admin/settings" element={<ProtectedRoute admin><AdminSettings /></ProtectedRoute>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
